// @flow

import { selectDomainFromFqdn, type ProductData } from '@gandi/cart';

import { useGetHostingPayload } from 'src/WebHosting';
import { useSimplehostingPrices } from 'src/WebHosting/modules/prices';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useIsEligibleForEssential } from 'src/hooks/useEligibleCustomer';
import type {
  WebhostingSize,
  SimplehostingPriceSummary,
} from 'src/modules/simplehosting/prices';

const findOffer =
  (duration: number, size: WebhostingSize) =>
  (price: SimplehostingPriceSummary) =>
    price.min_duration === duration && price.product.name === size;

export const useOffer = (fqdn: string): SimplehostingPriceSummary | void => {
  const { isFetching, isFailure, isLoaded, summaries } =
    useSimplehostingPrices();

  const isEligibleForEssential = useIsEligibleForEssential(fqdn);

  if (!isLoaded || isFailure || isFetching || !summaries) return undefined;

  return (
    (isEligibleForEssential && summaries.find(findOffer(12, 'essential'))) ||
    summaries.find(findOffer(12, 'starter'))
  );
};

export const useProductData = ({
  fqdn,
  packId,
}: {
  fqdn: string,
  packId: string,
}): ProductData => {
  const stateDomain = useAppSelector(state =>
    selectDomainFromFqdn(state, fqdn),
  );

  const { getHostingPayload } = useGetHostingPayload();

  const offer = useOffer(fqdn);

  // fallback when webhosting resource is not yet available
  // with minimum data required in getHostingPayload
  const chosenOffer = offer || {
    min_duration: 12,
    product: { name: 'starter' },
  };

  const basePayload = getHostingPayload({
    application: undefined, // else we should pass though useHostingApplications
    chosenOffer,
    packId,
    fqdn,
  });
  const domain = (!!stateDomain?.fqdn && stateDomain) || { fqdn };

  return {
    // $FlowFixMe[prop-missing]
    payload: {
      ...basePayload,
      // $FlowFixMe[prop-missing] the domain entry does not defined every field of real domain
      // $FlowFixMe[incompatible-exact]
      // $FlowFixMe[incompatible-return] we test before that domain is correct
      domain,
    },
    process: 'create',
    product_type: 'paas',
  };
};
